import React, { useEffect, useRef, memo } from 'react';

const ParticleNetwork = ({
  colors = { background: '#F8F9FA', accent: '#DDD6FE' },
  particleDensity = 12000,
  connectionDistance = 100,
  particleSpeed = 0.3,
  className = '',
  style = {}
}) => {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const particlesRef = useRef([]);
  const isMobileRef = useRef(false);
  const runningRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particlesArray = [];

    // Detect mobile to reduce particle count
    isMobileRef.current =
      window.matchMedia('(max-width: 768px)').matches ||
      window.devicePixelRatio > 2;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    class Particle {
      constructor() {
        this.x = Math.random() * (canvas?.width || 800);
        this.y = Math.random() * (canvas?.height || 600);
        this.size = 1.5;
        this.speedX = (Math.random() * 2 - 1) * particleSpeed;
        this.speedY = (Math.random() * 2 - 1) * particleSpeed;
      }

      update() {
        if (!canvas) return;
        if (this.x > canvas.width || this.x < 0) this.speedX = -this.speedX;
        if (this.y > canvas.height || this.y < 0) this.speedY = -this.speedY;
        this.x += this.speedX;
        this.y += this.speedY;
      }

      draw() {
        if (!ctx) return;
        ctx.fillStyle = colors.accent;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Spatial grid for O(n) connection lookups instead of O(n²)
    const buildGrid = (cellSize) => {
      const grid = new Map();
      for (let i = 0; i < particlesArray.length; i++) {
        const p = particlesArray[i];
        const gx = Math.floor(p.x / cellSize);
        const gy = Math.floor(p.y / cellSize);
        const key = gx + ',' + gy;
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(i);
      }
      return grid;
    };

    const connect = () => {
      if (particlesArray.length === 0) return;
      const cellSize = connectionDistance;
      const grid = buildGrid(cellSize);

      for (const [key, cell] of grid.entries()) {
        const [gx, gy] = key.split(',').map(Number);

        // Check this cell and 8 neighbors
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const neighborKey = (gx + dx) + ',' + (gy + dy);
            const neighborCell = grid.get(neighborKey);
            if (!neighborCell) continue;

            for (const a of cell) {
              for (const b of neighborCell) {
                if (a >= b) continue; // avoid duplicate pairs
                const pA = particlesArray[a];
                const pB = particlesArray[b];
                if (!pA || !pB) continue;

                const ddx = pA.x - pB.x;
                const ddy = pA.y - pB.y;
                const distance = Math.sqrt(ddx * ddx + ddy * ddy);

                if (distance < connectionDistance) {
                  const opacity = 1 - distance / connectionDistance;
                  ctx.strokeStyle = `rgba(${hexToRgb(colors.accent)}, ${opacity * 0.5})`;
                  ctx.lineWidth = 0.5;
                  ctx.beginPath();
                  ctx.moveTo(pA.x, pA.y);
                  ctx.lineTo(pB.x, pB.y);
                  ctx.stroke();
                }
              }
            }
          }
        }
      }
    };

    const init = () => {
      if (!canvas || !canvas.width || !canvas.height) return;
      particlesArray = [];
      // Mobile gets 10x fewer particles
      const effectiveDensity = isMobileRef.current ? particleDensity * 10 : particleDensity;
      const numberOfParticles = Math.max(10, (canvas.height * canvas.width) / effectiveDensity);
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
      particlesRef.current = particlesArray;
    };

    const animate = () => {
      if (!runningRef.current || !canvas || !ctx) return;
      if (!canvas.width || !canvas.height || !particlesArray || particlesArray.length === 0) {
        animFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const particle of particlesArray) {
        if (particle && typeof particle.update === 'function' && typeof particle.draw === 'function') {
          particle.update();
          particle.draw();
        }
      }

      connect();
      animFrameRef.current = requestAnimationFrame(animate);
    };

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0,0,0';
    };

    resizeCanvas();
    init();
    animate();

    const handleResize = () => {
      resizeCanvas();
      init();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      runningRef.current = false;
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [colors, particleDensity, connectionDistance, particleSpeed]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute top-0 left-0 w-full h-full z-0 ${className}`}
      style={{ backgroundColor: colors.background, ...style }}
      aria-hidden="true"
    />
  );
};

export default memo(ParticleNetwork);
